import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { PlaceCodeSortConfigDisabledTargetInput, useGetPlaceCodesAndSortsLazyQuery, useUpdatePlaceCodeAndSortsDisabledMutation } from '../generated/graphql';
import { HatchImage, LineImage } from '../components/ImageSelect';
import { Loading } from '../Loading';
import { AuthButton, Button } from '../components/Button';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { SectionHeader } from '../components/SectionHeader';
import { Checkbox } from '../components/Input';
import {
  buildCodeAndSortRows,
  CodeSortRow,
  isAllSelected,
  toDisabledTargets,
  toggleAllSelection,
  toggleRowSelection,
  toRowKey,
  toSelectionKeys,
} from './PlaceCodeAndSortPage.logic';
import { ToastContext } from '../components/Toast';
import { AppContext } from '../AppContext';
import './PlaceCodeAndSortPage.css';
import { TableScroll } from '../components/TableScroll';

type IPlaceCodeAndSortPageProps = RouteComponentProps<{ placeId: string }> & {};

/** 行に付けるクラス名。無効な組み合わせの行は文字を薄くする */
function getRowClassName(disabled: boolean): string | undefined {
  if (disabled) {
    return 'PlaceCodeAndSortPage-disabled_row';
  }
  return undefined;
}

/** まとめての操作が成功したときの通知の文言 */
function getBulkSuccessMessage(disabled: boolean, count: number): string {
  if (disabled) {
    return `${count} 件を無効化しました`;
  }
  return `${count} 件を有効化しました`;
}

export const PlaceCodeAndSortPage: React.FC<IPlaceCodeAndSortPageProps> = function (props) {
  const placeId = parseInt(props.match.params.placeId);
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const appContext = React.useContext(AppContext);

  const [getCodesAndSorts, getCodesAndSortsResult] = useGetPlaceCodesAndSortsLazyQuery({
    variables: { placeId }
  });
  // 1行の切り替えも、まとめての切り替えも、同じミューテーションで送る。成功と失敗の知らせ方が違うので、結果は呼び出した側で扱う
  const [updateDisabled, updateDisabledResult] = useUpdatePlaceCodeAndSortsDisabledMutation();

  const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);
  // 物件が切り替わったときに選択を引き継がない
  React.useEffect(() => {
    setSelectedKeys([]);
  }, [placeId]);

  if (!getCodesAndSortsResult.called) {
    getCodesAndSorts();
  }

  if (getCodesAndSortsResult.error) {
    return <GraphQLErrorDialog error={getCodesAndSortsResult.error} />;
  }

  if (getCodesAndSortsResult.loading || !getCodesAndSortsResult.data) {
    return <Loading />;
  }

  const codeAndSortConfigs = buildCodeAndSortRows(getCodesAndSortsResult.data.getPlace);

  // 選択と、まとめての操作ができるのは、有効・無効を切り替えられる権限の段階2以上の人だけ
  const me = appContext.state.me;
  const canEdit = me !== undefined && me.authority.level >= 2;
  const bulkTargets = toDisabledTargets(selectedKeys, codeAndSortConfigs);
  const bulkBarVisible = canEdit && bulkTargets.length > 0;
  const isMutationRunning = updateDisabledResult.loading;

  /**
   * 1行の組み合わせの有効・無効を切り替える。
   * 剥落防止の設定がある補修コードは、浮き面積の基準以上と未満の2つの組み合わせをそろえて切り替える。
   */
  async function toggleDisabled(row: CodeSortRow, disabled: boolean) {
    const targets = toDisabledTargets(toSelectionKeys(row), codeAndSortConfigs);
    // 切り替えた結果は一覧の表示で分かるので、成功したときは知らせず、失敗したときだけ通知する
    try {
      await updateDisabled({ variables: { placeId, disabled: !disabled, targets } });
    } catch (error) {
      toastContext.showToast('切り替えできませんでした。時間をおいて再実行してください。', 'error');
    }
    getCodesAndSorts();
  }

  /** 選んだ組み合わせをまとめて無効化・有効化する。一括操作なので、成功したときも通知する */
  async function updateSelectedDisabled(disabled: boolean, targets: PlaceCodeSortConfigDisabledTargetInput[]) {
    if (targets.length === 0) {
      return;
    }
    try {
      await updateDisabled({ variables: { placeId, disabled, targets } });
      toastContext.showToast(getBulkSuccessMessage(disabled, targets.length), 'success', 3000);
      setSelectedKeys([]);
    } catch (error) {
      // 失敗したときは選択を残し、そのまま再実行できるようにする
      toastContext.showToast('まとめて切り替えできませんでした。時間をおいて再実行してください。', 'error');
    }
    getCodesAndSorts();
  }

  let headerClassName = 'PlaceCodeAndSortPage-header';
  if (bulkBarVisible) {
    headerClassName = 'PlaceCodeAndSortPage-header covered';
  }

  return (
    <div className="PlaceCodeAndSortPage-container">
      <Loading open={isMutationRunning} />
      {bulkBarVisible && (
        <div className="PlaceCodeAndSortPage-bulk_anchor">
          <div className="PlaceCodeAndSortPage-bulk_bar">
            <span className="PlaceCodeAndSortPage-bulk_count">{bulkTargets.length} 件選択中</span>
            <div className="PlaceCodeAndSortPage-bulk_actions">
              <Button color="normal" mini disabled={isMutationRunning} onClick={() => updateSelectedDisabled(false, bulkTargets)}>
                有効化
              </Button>
              <Button color="red" mini disabled={isMutationRunning} onClick={() => updateSelectedDisabled(true, bulkTargets)}>
                無効化
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* 選択中はこの行に選択中のバーが重なるので、見出しの端がはみ出して見えないよう行ごと隠す */}
      <div className={headerClassName}>
        <SectionHeader title="分類 × コード" />
      </div>
      <p className="App-table_count">{codeAndSortConfigs.length} 件</p>
      <TableScroll storageKey="placeCodesAndSorts">
        <table className="App-table PlaceCodeAndSortPage-table">
          <thead>
            <tr>
              {canEdit && (
                <th className="PlaceCodeAndSortPage-check_cell" data-column-resize="off">
                  <Checkbox
                    value={isAllSelected(selectedKeys, codeAndSortConfigs)}
                    disabled={isMutationRunning}
                    onChange={() => {
                      setSelectedKeys(toggleAllSelection(selectedKeys, codeAndSortConfigs));
                    }}
                  />
                </th>
              )}
              <th>補修分類</th>
              <th>補修コード</th>
              <th>前置文字列</th>
              <th>画層の色</th>
              <th>線種</th>
              <th>テクスチャ</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
          {codeAndSortConfigs.map((args => {
            const { code, sort, config, gteEverguardThreshold } = args;

            const prefix = config?.prefix ?? "";
            const color = config?.color ?? '#FFFFFF';
            const lineType = config?.line_type ?? 0;
            const hatchType = config?.hatch_type ?? 0;
            const disabled = config?.disabled ?? false;

            return (
              <tr key={toRowKey(args)} className={getRowClassName(disabled)}>
                {canEdit && (
                  <td className="PlaceCodeAndSortPage-check_cell">
                    <Checkbox
                      value={selectedKeys.includes(toRowKey(args))}
                      disabled={isMutationRunning}
                      onChange={() => {
                        setSelectedKeys(toggleRowSelection(selectedKeys, args));
                      }}
                    />
                  </td>
                )}
                <td>{sort.name}</td>
                <td>
                  {code.name}
                  {(function () {
                    if (!disabled) {
                      return null;
                    }
                    return <span className="PlaceCodeAndSortPage-disabled_badge">無効</span>;
                  })()}
                </td>
                <td>{prefix}</td>
                <td>
                  <div className="PlaceCodeAndSortPage-color">
                    <span
                      className="PlaceCodeAndSortPage-color_swatch"
                      style={{ backgroundColor: color }}
                    />
                    <span>{color}</span>
                  </div>
                </td>
                <td>
                  <div className="PlaceCodeAndSortPage-image">
                    <LineImage id={lineType} />
                  </div>
                </td>
                <td>
                  <div className="PlaceCodeAndSortPage-image">
                    <HatchImage id={hatchType} />
                  </div>
                </td>
                <td className="App-table_button_cell">
                  <div className="App-table_actions">
                    <AuthButton
                      authLv={2}
                      onClick={() => {
                        history.push(`/places/${placeId}/codes_and_sorts/${code.id ?? 'none'}/${sort.id ?? 'none'}/${gteEverguardThreshold}/edit`);
                      }}
                      mini={true}
                      color="white"
                    >
                      編集
                    </AuthButton>
                    {(function () {
                      if (disabled) {
                        return (
                          <AuthButton authLv={2} mini={true} color="normal" disabled={isMutationRunning} onClick={() => toggleDisabled(args, disabled)}>
                            有効化
                          </AuthButton>
                        );
                      }
                      return (
                        <AuthButton authLv={2} mini={true} color="red" disabled={isMutationRunning} onClick={() => toggleDisabled(args, disabled)}>
                          無効化
                        </AuthButton>
                      );
                    })()}
                  </div>
                </td>
              </tr>
            );
          }))}
          </tbody>
        </table>
      </TableScroll>
    </div>
  );
};
